import { IonContent } from '@ionic/react';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { amplitudeModule } from '@/modules/amplitude';
import { webCampaign } from '@/modules/webCampaign';
import { NavigationRange } from '@/pages/Onboarding/components/NavigationRange';
import { OptionItemSquareWithIconList } from '@/pages/Onboarding/components/OptionItemSquareWithIcon';
import { OptionItemSquareWithSmile } from '@/pages/Onboarding/components/OptionItemSquareWithSmile';
import { useOnboarding } from '@/pages/Onboarding/context';
import { MAJOR_TURN_OFF_OPTIONS } from '@/pages/Onboarding/options/MajorTurnOff/MAJOR_TURN_OFF_OPTIONS';
import { OnbOptionType } from '@/pages/Onboarding/types';
import { nextStepTimeout } from '@/pages/Onboarding/utils/nextStepTimeout';
import { userService } from '@/services/user';
import { canLogEvents } from '@/utils/canLogEvents';
import { preloadImage } from '@/utils/preloadImage';
import { preloadVideo } from '@/utils/preloadVideo';

import { OwnAiTitle } from '../../../OwnAISecond/components/Title';
import { useLayoutFormSelections } from '../../../OwnAISecond/hooks/useLayoutFormSelections';
import { LayoutDataType } from '../../types';

import { MajorTurnOffStyles } from './MajorTurnOff.styles';

export const ScreenMajorTurnOff: React.FC = () => {
  React.useEffect(() => {
    if (canLogEvents()) {
      amplitudeModule.logEvent('onboarding_screen', {
        seen: 'MajorTurnOff',
      });
    }
  }, []);

  const shouldAnimate = webCampaign.isAnimated();
  const [showElements, setShowElements] = React.useState<boolean>(true);

  const { wizard } = useOnboarding();

  const form = useFormContext<LayoutDataType>();
  const major_turn_off = form.watch('major_turn_off');

  const { ageGeneration, bodyShape, hairColor } = useLayoutFormSelections();

  React.useEffect(() => {
    preloadImage(
      `https://d3hhlnlnie19wm.cloudfront.net/web-onb/assets/layout-own-ai-second-video/${ageGeneration}/${bodyShape}/${hairColor}/send_photos_3.png`,
    );
    preloadVideo(
      `https://d3hhlnlnie19wm.cloudfront.net/web-onb/assets/layout-own-ai-second-video/${ageGeneration}/${bodyShape}/${hairColor}/video.webm`,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (
    option: OnbOptionType<LayoutDataType['major_turn_off']>,
  ) => {
    form.setValue('major_turn_off', option.id);
    userService.createOrUpdate({
      onboarding_data: {
        major_turn_off: option.title,
      },
    });

    if (shouldAnimate) {
      setTimeout(() => {
        setShowElements(false);
        nextStepTimeout(wizard.nextStep);
      }, 300);
    } else {
      nextStepTimeout(wizard.nextStep);
    }
  };

  const handleBackStep = (): void => {
    if (shouldAnimate) {
      setShowElements(false);
      nextStepTimeout(() => wizard.goToStep('personality'));
    } else {
      wizard.goToStep('personality');
    }
  };

  return (
    <MajorTurnOffStyles background="dark-violet">
      <NavigationRange onBack={handleBackStep} />

      <IonContent>
        <OwnAiTitle animate={shouldAnimate} show={showElements}>
          What is the major turn-off for you?
        </OwnAiTitle>
        <OptionItemSquareWithIconList>
          {MAJOR_TURN_OFF_OPTIONS.map((option) => (
            <OptionItemSquareWithSmile
              isSelected={major_turn_off === option.id}
              key={option.id}
              onClick={() => handleChange(option)}
              title={option.title}
              subtitle={option.subtitle}
              animate={shouldAnimate}
              TEST_ID={`major_turn_off-${option.id}`}
              show={showElements}
              smileImage={option.icon}
            />
          ))}
        </OptionItemSquareWithIconList>
      </IonContent>
    </MajorTurnOffStyles>
  );
};
